import type {ChangeEvent} from 'react'
import React, {memo, useMemo, useState} from 'react'

import useEventCallback from '../../../../../hooks/useEventCallback'
import Loader from '../../../../common/Loader/Loader'
import {ParametersGroupControls} from '../../../../packages/Parameters/components/GroupControls/ParametersGroupControls'
import {ParametersPageHeader} from '../../../../packages/Parameters/components/PageHeader/ParametersPageHeader'
import {ParametersTree} from '../../../../packages/Parameters/components/ParametersTree/ParametersTree'
import {ParametersSearch} from '../../../../packages/Parameters/components/Search/ParametersSearch'
import {ParameterCategory, ParameterGroup} from '../../../../packages/Parameters/Parameters.types'
import {filterSearch} from '../../../../packages/Parameters/utils/filterSearch'

import {useAgentTypeParameters} from './hooks/useAgentTypeParameters'

import {inlineListMaxLength} from 'src/components/packages/Parameters/Parameters.constants'

interface Props {
  agentTypeId: number
}

function AgentTypeParametersComponent(props: Props) {
  const {agentTypeId} = props
  const {isLoading, configurationParameters, systemParameters, environmentParameters} =
    useAgentTypeParameters(agentTypeId)

  const [search, setSearch] = useState('')

  const parameters = useMemo(
    () => [
      {
        category: ParameterCategory.AGENT_TYPE,
        groups: [
          {
            group: ParameterGroup.CONFIGURATION,
            withDescription: true,
            header: {
              value: 'Value',
            },
            items: filterSearch({
              parameters: configurationParameters?.property ?? [],
              search,
            }),
          },
          {
            group: ParameterGroup.SYSTEM,
            withDescription: true,
            header: {
              value: 'Value',
            },
            items: filterSearch({
              parameters: systemParameters?.property ?? [],
              search,
            }),
          },
          {
            group: ParameterGroup.ENVIRONMENT,
            withDescription: true,
            header: {
              value: 'Value',
            },
            items: filterSearch({
              parameters: environmentParameters?.property ?? [],
              search,
            }),
          },
        ],
      },
    ],
    [
      configurationParameters?.property,
      environmentParameters?.property,
      search,
      systemParameters?.property,
    ],
  )

  const handleChangeSearch = useEventCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.currentTarget.value)
  })

  const handleClearSearch = useEventCallback(() => {
    setSearch('')
  })

  const totalParametersCount =
    (configurationParameters?.count ?? 0) +
    (systemParameters?.count ?? 0) +
    (environmentParameters?.count ?? 0)
  const isVirtualized = totalParametersCount > inlineListMaxLength

  return (
    <>
      <ParametersGroupControls />
      <ParametersPageHeader>
        <ParametersSearch
          value={search}
          onChange={handleChangeSearch}
          onClear={handleClearSearch}
        />
      </ParametersPageHeader>

      {isLoading ? (
        <Loader />
      ) : (
        <ParametersTree highlight={search} parameters={parameters} isVirtualized={isVirtualized} />
      )}
    </>
  )
}

export const AgentTypeParameters = memo(AgentTypeParametersComponent)
